const DEFAULT_RECURRENCE_SECONDS = 31536000;
const DEFAULT_GRACE_SECONDS = 1209600;
const CADENCE_LABELS = new Map([
  ['onboarding_only', 'onboarding only'],
  ['event_triggered', 'event triggered'],
  ['periodic_and_event', 'periodic and event triggered'],
  ['periodic', 'periodic'],
]);

export const VERIFICATION_TYPE_OPTIONS = [
  { label: 'KYC', value: 'kyc' },
  { label: 'AML', value: 'aml' },
  { label: 'Identity', value: 'identity' },
  { label: 'Credit', value: 'credit' },
  { label: 'CIPC', value: 'cipc' },
  { label: 'Bank Statement', value: 'bank_statement' },
  { label: 'Custom', value: 'custom' },
];

export const CADENCE_KIND_OPTIONS = [
  { label: 'Onboarding only', value: 'onboarding_only' },
  { label: 'Periodic (repeats)', value: 'periodic' },
  { label: 'Event triggered', value: 'event_triggered' },
  { label: 'Periodic and event triggered', value: 'periodic_and_event' },
];

export const RECURRENCE_INTERVAL_OPTIONS = [
  { label: 'Daily (1 day)', seconds: 86400 },
  { label: 'Weekly (7 days)', seconds: 604800 },
  { label: 'Bi-weekly (14 days)', seconds: 1209600 },
  { label: 'Monthly (30 days)', seconds: 2592000 },
  { label: 'Quarterly (90 days)', seconds: 7776000 },
  { label: 'Semi-annually (182 days)', seconds: 15724800 },
  { label: 'Annually (365 days)', seconds: 31536000 },
];

export const GRACE_PERIOD_OPTIONS = [
  { label: '1 day (1 day)', seconds: 86400 },
  { label: '3 days (3 days)', seconds: 259200 },
  { label: '1 week (7 days)', seconds: 604800 },
  { label: '2 weeks (14 days)', seconds: 1209600 },
  { label: '1 month (30 days)', seconds: 2592000 },
];

export function format_seconds(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return '0';
  return seconds.toLocaleString();
}

export function format_days_from_seconds(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  const days = Math.round(seconds / 86400);
  const label = days === 1 ? 'day' : 'days';
  return `${days.toLocaleString()} ${label}`;
}

export function format_cadence_kind(value) {
  const cadence = String(value || '').trim().toLowerCase();
  const result = CADENCE_LABELS.get(cadence) || cadence || '—';
  return result;
}

export function positive_number_or_null(value) {
  const number_value = Number(value);
  const result = Number.isFinite(number_value) && number_value > 0 ? number_value : null;
  return result;
}

export function get_response_items(response) {
  const data = response?.data || {};
  const items = data.data || data.results;
  const result = Array.isArray(items) ? items : [];
  return result;
}

export function get_response_data(response) {
  const data = response?.data || {};
  const result = data.data || data || {};
  return result;
}

export function is_success_response(response) {
  const result = response?.data?.status === 'success';
  return result;
}

export function get_normalized_bindings_from_response(response) {
  if (!is_success_response(response)) return [];
  const result = (Array.isArray(response.data.data) ? response.data.data : [])
    .map((binding) => normalize_binding(binding))
    .filter((binding) => binding.id);
  return result;
}

export function get_entity_config_id_from_route(route) {
  const query = route?.query || {};
  const raw_id = Reflect.get(query, 'entityConfigId') || Reflect.get(query, 'entity_config_id');
  const result = positive_number_or_null(raw_id);
  return result;
}

export function resolve_entity_config_id(prop_id, local_id, route) {
  const candidates = [
    positive_number_or_null(prop_id),
    positive_number_or_null(local_id),
    get_entity_config_id_from_route(route),
  ];
  const result = candidates.find((candidate) => candidate) || null;
  return result;
}

export function normalize_verification_form(form) {
  if (!form || typeof form !== 'object') return null;
  const result = {
    id: get_first_value(form, ['verification_form_id', 'id']),
    name: get_first_value(form, ['form_name', 'name']) || 'Verification form',
  };
  return result;
}

export function normalize_manual_verification_form(form) {
  if (!form || typeof form !== 'object') return null;
  const result = {
    id: get_first_value(form, ['manual_verification_form_id', 'id']),
    name: get_first_value(form, ['form_name', 'name']) || 'Manual verification form',
  };
  return result;
}

export function normalize_manual_form_link(link, manual_form) {
  if (!link || typeof link !== 'object') return null;
  const id = get_first_value(link, ['manual_form_link_id', 'form_link_id', 'id']);
  if (!id) return null;
  const result = {
    id,
    manual_verification_form_name: get_manual_verification_form_name(link, manual_form),
    onboarding_form_name: get_onboarding_form_name(link),
    is_active: Reflect.get(link, 'is_active') !== false,
  };
  return result;
}

export function normalize_onboarding_form_link(link, verification_form) {
  if (!link || typeof link !== 'object') return null;
  const id = get_first_value(link, ['onboarding_form_link_id', 'form_link_id', 'id']);
  if (!id) return null;
  const result = {
    id,
    verification_form_name: get_verification_form_name(link, verification_form),
    onboarding_form_name: get_onboarding_form_name(link),
    is_active: Reflect.get(link, 'is_active') !== false,
  };
  return result;
}

export function normalize_binding(binding) {
  const result = {
    id: binding.id,
    verification_type: binding.verification_type,
    cadence_kind: binding.cadence_kind,
    manual_form_link_id: binding.manual_form_link || binding.manual_form_link_id,
    onboarding_form_link_id: binding.onboarding_form_link || binding.onboarding_form_link_id,
    recurrence_interval_seconds: binding.recurrence_interval_seconds,
    grace_period_seconds: binding.grace_period_seconds,
    onboarding_form_name: binding.onboarding_form_name,
    verification_form_name: binding.verification_form_name,
    manual_verification_form_name: binding.manual_verification_form_name,
  };
  return result;
}

export function get_default_binding_form() {
  const result = {
    verification_type: 'kyc',
    manual_form_link_id: '',
    cadence_kind: 'onboarding_only',
    recurrence_interval_seconds: DEFAULT_RECURRENCE_SECONDS,
    grace_period_seconds: DEFAULT_GRACE_SECONDS,
  };
  return result;
}

export function get_binding_form_from_binding(binding) {
  const safe_binding = binding || {};
  const result = {
    verification_type: get_string_field_or_default(safe_binding, 'verification_type', 'kyc'),
    manual_form_link_id: get_positive_field_or_default(safe_binding, 'manual_form_link_id', ''),
    cadence_kind: get_string_field_or_default(safe_binding, 'cadence_kind', 'onboarding_only'),
    recurrence_interval_seconds: get_positive_field_or_default(
      safe_binding,
      'recurrence_interval_seconds',
      DEFAULT_RECURRENCE_SECONDS,
    ),
    grace_period_seconds: get_positive_field_or_default(
      safe_binding,
      'grace_period_seconds',
      DEFAULT_GRACE_SECONDS,
    ),
  };
  return result;
}

export function build_binding_payload(entity_config_id, binding_form, is_periodic) {
  const safe_form = binding_form || {};
  const base_payload = {
    entity_config_id,
    verification_type: get_string_field_or_default(safe_form, 'verification_type', '').trim(),
    manual_form_link_id: get_number_field_or_default(safe_form, 'manual_form_link_id', null),
    cadence_kind: get_string_field_or_default(safe_form, 'cadence_kind', 'onboarding_only'),
  };
  const result = is_periodic ? add_periodic_fields(base_payload, binding_form) : base_payload;
  return result;
}

function add_periodic_fields(payload, binding_form) {
  const safe_form = binding_form || {};
  const result = {
    ...payload,
    recurrence_interval_seconds: Number(
      Reflect.get(safe_form, 'recurrence_interval_seconds'),
    ) || 0,
    grace_period_seconds: Number(Reflect.get(safe_form, 'grace_period_seconds')) || 0,
  };
  return result;
}

function get_first_value(source, keys) {
  const result = keys.map((key) => Reflect.get(source, key)).find((value) => value);
  return result;
}

function get_verification_form_name(link, verification_form) {
  const result = get_first_value(link, ['verification_form_name'])
    || Reflect.get(verification_form || {}, 'name')
    || 'Verification form';
  return result;
}

function get_manual_verification_form_name(link, manual_form) {
  const result = get_first_value(link, ['manual_verification_form_name'])
    || Reflect.get(manual_form || {}, 'name')
    || 'Manual verification form';
  return result;
}

function get_onboarding_form_name(link) {
  const result = get_first_value(link, ['onboarding_form_name']) || 'Onboarding form';
  return result;
}

function get_string_field_or_default(source, field, default_value) {
  const result = String(Reflect.get(source, field) || default_value);
  return result;
}

function get_number_field_or_default(source, field, default_value) {
  const result = Number(Reflect.get(source, field)) || default_value;
  return result;
}

function get_positive_field_or_default(source, field, default_value) {
  const value = positive_number_or_null(Reflect.get(source, field));
  const result = value || default_value;
  return result;
}

export function get_binding_validation_message(binding_form) {
  const verification_type = String(binding_form?.verification_type || '').trim();
  if (!verification_type) return 'Please enter a verification type (e.g. kyc).';
  if (!Number(binding_form?.manual_form_link_id)) {
    return 'Please select the mapped manual verification form this rule should use.';
  }
  return '';
}

export function should_remove_original_binding(binding_id, original_type, next_type) {
  const has_changed_type = original_type && original_type !== next_type;
  const result = Boolean(binding_id && has_changed_type);
  return result;
}
